import JsSIP from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { IncomingRTCSessionEvent } from 'jssip/lib/UA';
import { api } from './api';
import { startRinging, stopRinging } from './sounds';
import { useNova } from './store';
import type { SoftphoneConfig } from './types';

/**
 * The browser softphone. Registers to Asterisk over WebSocket (SIP over WSS
 * in production), answers the calls the backend sends to this agent, and
 * exposes live audio levels for the signal ring. Call control (hold,
 * transfer, hang-up) goes through the API; SIP here only carries media.
 */
/** What an agent sees when answering on this device fails, by JsSIP cause. */
function answerFailure(cause?: string): string {
  if (cause === JsSIP.C.causes.USER_DENIED_MEDIA_ACCESS) {
    return 'Your microphone is blocked. Allow it for this site in the browser, then take the next call.';
  }
  if (cause === JsSIP.C.causes.CANCELED || cause === JsSIP.C.causes.REQUEST_TIMEOUT) {
    return 'The call stopped ringing before it connected. Check your network; it will be offered again.';
  }
  return `Could not answer the call${cause ? ` (${cause})` : ''}.`;
}

class Softphone {
  private ua: JsSIP.UA | null = null;
  private session: RTCSession | null = null;
  private cfg: SoftphoneConfig | null = null;
  private remoteAudio = new Audio();
  private ctx: AudioContext | null = null;
  private remoteAnalyser: AnalyserNode | null = null;
  private localAnalyser: AnalyserNode | null = null;

  constructor() {
    this.remoteAudio.autoplay = true;
  }

  async start(): Promise<void> {
    this.stop();
    const store = useNova.getState();
    let cfg: SoftphoneConfig;
    try {
      cfg = await api<SoftphoneConfig>('GET', '/me/softphone');
    } catch (err) {
      store.set({ phone: 'failed', phoneError: (err as Error).message });
      return;
    }
    if (!cfg.enabled) {
      store.set({ phone: 'off', phoneError: 'No extension on your user' });
      return;
    }
    this.cfg = cfg;
    store.set({ phone: 'connecting', phoneError: null });

    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const socket = new JsSIP.WebSocketInterface(`${scheme}://${location.host}${cfg.wsPath}`);
    const ua = new JsSIP.UA({
      sockets: [socket],
      uri: cfg.uri,
      authorization_user: cfg.username,
      password: cfg.password,
      display_name: cfg.displayName,
      register: true,
      register_expires: 300,
      session_timers: false,
      user_agent: 'Nova Agent Cockpit',
    });
    this.ua = ua;

    ua.on('registered', () => useNova.getState().set({ phone: 'registered', phoneError: null }));
    ua.on('unregistered', () => useNova.getState().set({ phone: 'connecting' }));
    ua.on('disconnected', () => useNova.getState().set({ phone: 'connecting' }));
    ua.on('registrationFailed', (e: { cause?: string }) =>
      useNova.getState().set({ phone: 'failed', phoneError: `Softphone sign-in failed: ${e.cause ?? 'unknown'}` }),
    );
    ua.on('newRTCSession', (e: IncomingRTCSessionEvent) => this.onSession(e));
    ua.start();
  }

  stop(): void {
    this.hangupLocal();
    this.ua?.stop();
    this.ua = null;
    useNova.getState().set({ phone: 'off', ringing: null });
  }

  private onSession(e: IncomingRTCSessionEvent): void {
    if (e.originator !== 'remote') return;
    const session = e.session;
    if (this.session && !this.session.isEnded()) {
      session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      return;
    }
    this.session = session;
    const interactionId = e.request.getHeader('X-Nova-Interaction') || null;
    const autoAnswer = e.request.getHeader('X-Nova-Auto-Answer') === 'true';
    const from = e.request.from.display_name || e.request.from.uri.user;
    const testNumber = e.request.getHeader('X-Nova-Test-Call') || null;
    // A test call opens on the workspace, where its keypad is
    if (testNumber) useNova.getState().set({ testCall: { number: testNumber, since: null, muted: false }, view: 'cockpit' });

    session.on('peerconnection', ({ peerconnection }) => {
      peerconnection.addEventListener('track', (ev) => this.attachRemote(ev.streams[0]));
    });
    // Answer once a relay route is ready (or after 3 s) instead of waiting for every
    // ICE server to finish: one slow or unreachable route can otherwise run past the ring timeout
    let iceTimer: ReturnType<typeof setTimeout> | null = null;
    session.on('icecandidate', ({ candidate, ready }: { candidate: RTCIceCandidate; ready: () => void }) => {
      if (candidate.type === 'relay') ready();
      else iceTimer ??= setTimeout(ready, 3000);
    });
    session.on('ended', () => this.cleanup(session));
    session.on('failed', (ev: { originator: string; cause?: string }) => {
      // Picking up failed on this device: say why, or the agent sees only that the call went away
      if (useNova.getState().ringing?.answering) useNova.getState().toast(answerFailure(ev.cause), 'error');
      this.cleanup(session);
    });
    session.on('accepted', () => {
      stopRinging();
      const test = useNova.getState().testCall;
      useNova.getState().set({ ringing: null, testCall: test ? { ...test, since: Date.now() } : null });
      const pc = session.connection;
      const local = pc?.getSenders().find((s) => s.track)?.track;
      if (local) this.attachLocal(new MediaStream([local]));
    });

    if (autoAnswer) {
      this.answer();
    } else {
      useNova.getState().set({ ringing: { interactionId, from } });
      startRinging();
    }
  }

  answer(): void {
    const s = this.session;
    if (!s || s.isEstablished()) return;
    stopRinging();
    const ringing = useNova.getState().ringing;
    if (ringing) useNova.getState().set({ ringing: { ...ringing, answering: true } });
    s.answer({
      mediaConstraints: { audio: true, video: false },
      pcConfig: { iceServers: this.cfg?.iceServers ?? [] },
    });
  }

  decline(): void {
    stopRinging();
    this.session?.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
    useNova.getState().set({ ringing: null });
  }

  /** Local hang-up, used only if the API is unreachable. */
  hangupLocal(): void {
    if (this.session && !this.session.isEnded()) this.session.terminate();
  }

  /** Mutes this device's microphone (test calls and a colleague's call to me, which the API does not control for me). */
  setMuted(muted: boolean): void {
    this.session?.connection?.getSenders().forEach((s) => {
      if (s.track?.kind === 'audio') s.track.enabled = !muted;
    });
    const test = useNova.getState().testCall;
    useNova.getState().set({ phoneMuted: muted, testCall: test ? { ...test, muted } : null });
  }

  private cleanup(session: RTCSession): void {
    if (this.session !== session) return;
    stopRinging();
    this.session = null;
    this.remoteAnalyser = null;
    this.localAnalyser = null;
    this.remoteAudio.srcObject = null;
    useNova.getState().set({ ringing: null, testCall: null, phoneMuted: false });
  }

  private audioCtx(): AudioContext {
    this.ctx ??= new AudioContext();
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  private attachRemote(stream: MediaStream): void {
    this.remoteAudio.srcObject = stream;
    void this.remoteAudio.play().catch(() => undefined);
    const a = this.audioCtx().createAnalyser();
    a.fftSize = 256;
    this.audioCtx().createMediaStreamSource(stream).connect(a);
    this.remoteAnalyser = a;
  }

  private attachLocal(stream: MediaStream): void {
    const a = this.audioCtx().createAnalyser();
    a.fftSize = 256;
    this.audioCtx().createMediaStreamSource(stream).connect(a);
    this.localAnalyser = a;
  }

  /** 0..1 loudness per band for the caller (remote) and the agent (local). */
  levels(bands: number): { remote: number[]; local: number[] } {
    const read = (an: AnalyserNode | null) => {
      if (!an) return new Array(bands).fill(0);
      const data = new Uint8Array(an.frequencyBinCount);
      an.getByteFrequencyData(data);
      const per = Math.floor(data.length / bands) || 1;
      return Array.from({ length: bands }, (_, i) => {
        let sum = 0;
        for (let j = 0; j < per; j++) sum += data[i * per + j] ?? 0;
        return sum / per / 255;
      });
    };
    return { remote: read(this.remoteAnalyser), local: read(this.localAnalyser) };
  }

  get inCall(): boolean {
    return Boolean(this.session?.isEstablished());
  }
}

export const softphone = new Softphone();
